import NumericInput from '../../components/NumericInput';
import { useState, useEffect, useMemo } from 'react';
import {
    Modal, ModalDialog, ModalClose,
    Box, Stack, Typography, Button, Input, IconButton, Chip,
} from '@mui/joy';
import LocalOfferRoundedIcon  from '@mui/icons-material/LocalOfferRounded';
import CategoryRoundedIcon    from '@mui/icons-material/CategoryRounded';
import Inventory2RoundedIcon  from '@mui/icons-material/Inventory2Rounded';
import PublicRoundedIcon      from '@mui/icons-material/PublicRounded';
import EditRoundedIcon        from '@mui/icons-material/EditRounded';

/**
 * Универсальный селектор скидки.
 *
 * Возвращает в onApply объект формата:
 *   {
 *     discount_id: number | null,   // null — ручная (нет в справочнике)
 *     name: string,
 *     value: number,
 *     is_percent: boolean,
 *   }
 *
 * Props:
 *   open: bool
 *   onClose: () => void
 *   onApply: (discount) => void
 *   discounts:  Discount[]         — справочник скидок (все активные)
 *   categories: ProductCategory[]  — справочник категорий (для группировки названий)
 *   product:    Product | null     — товар выбранной позиции (для фильтра скидок). Для target='order' игнорируется
 *   target:     'item' | 'order'   — куда применяется (влияет на заголовок и фильтры)
 */
export default function DiscountPickerModal({
    open, onClose, onApply,
    discounts = [], categories = [],
    product = null, target = 'item',
}) {
    const [mode, setMode] = useState('catalog');  // 'catalog' | 'manual'
    const [manualName, setManualName] = useState('');
    const [manualValue, setManualValue] = useState('');
    const [manualIsPercent, setManualIsPercent] = useState(false);

    useEffect(() => {
        if (open) {
            setMode('catalog');
            setManualName('');
            setManualValue('');
            setManualIsPercent(false);
        }
    }, [open]);

    // Фильтр скидок под контекст
    const availableDiscounts = useMemo(() => {
        return (discounts || []).filter(d => {
            if (!d.is_active) return false;
            const isUniversal = !d.product && !d.category;
            if (target === 'order') {
                // На чек: универсальные + по категориям (без привязки к конкретному товару)
                return isUniversal || !!d.category;
            }
            // На позицию
            if (isUniversal) return true;
            if (product && d.product === product.id) return true;
            if (product && product.category && d.category === product.category) return true;
            return false;
        });
    }, [discounts, product, target]);

    // Группируем
    const grouped = useMemo(() => {
        const byProduct = [];
        const byCategory = new Map();   // catId -> Discount[]
        const universal = [];
        for (const d of availableDiscounts) {
            if (d.product) byProduct.push(d);
            else if (d.category) {
                if (!byCategory.has(d.category)) byCategory.set(d.category, []);
                byCategory.get(d.category).push(d);
            } else universal.push(d);
        }
        return { byProduct, byCategory, universal };
    }, [availableDiscounts]);

    const categoryById = useMemo(
        () => Object.fromEntries((categories || []).map(c => [c.id, c])),
        [categories],
    );

    const pickFromCatalog = (d) => {
        onApply({
            discount_id: d.id,
            name: d.name,
            value: parseFloat(d.value),
            is_percent: !!d.is_percent,
        });
        onClose();
    };

    const applyManual = () => {
        const v = parseFloat(manualValue);
        if (isNaN(v) || v <= 0) return;
        onApply({
            discount_id: null,
            name: (manualName.trim() || 'Скидка').slice(0, 100),
            value: v,
            is_percent: manualIsPercent,
        });
        onClose();
    };

    return (
        <Modal open={open} onClose={onClose}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.6)' } } }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 520, width: 'calc(100vw - 32px)',
                maxHeight: '90vh', overflowY: 'auto',
                p: { xs: 2, sm: 3 },
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 0.3 }}>
                    <LocalOfferRoundedIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5, color: '#f59e0b' }} />
                    {target === 'order' ? 'Скидка на весь чек' : 'Скидка на позицию'}
                </Typography>
                {target === 'item' && product && (
                    <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-tertiary)', mb: 1.5 }}>
                        Товар: <b>{product.name}</b>
                    </Typography>
                )}

                {/* Переключатель режима */}
                <Stack direction="row" spacing={0.5} sx={{ mb: 2 }}>
                    {[
                        { v: 'catalog', label: 'Из справочника' },
                        { v: 'manual',  label: 'Ручная' },
                    ].map(opt => {
                        const active = mode === opt.v;
                        return (
                            <Box key={opt.v}
                                onClick={() => setMode(opt.v)}
                                sx={{
                                    flex: 1, py: 0.8, textAlign: 'center', cursor: 'pointer',
                                    borderRadius: 'var(--joy-radius-md)', fontSize: 12, fontWeight: 700,
                                    border: `1.5px solid ${active ? '#f59e0b' : 'var(--joy-palette-divider)'}`,
                                    bgcolor: active ? '#f59e0b' : 'var(--joy-palette-background-level2)',
                                    color:   active ? '#fff'    : 'var(--joy-palette-text-secondary)',
                                    transition: 'all .15s',
                                    '&:hover': { borderColor: '#f59e0b' },
                                }}>
                                {opt.label}
                            </Box>
                        );
                    })}
                </Stack>

                {/* ── Каталог ────────────────────────────────────── */}
                {mode === 'catalog' && (
                    <Stack spacing={1.5}>
                        {grouped.byProduct.length > 0 && (
                            <Box>
                                <Typography sx={sectionLabelSx}>
                                    <Inventory2RoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                    На этот товар
                                </Typography>
                                <Stack spacing={0.6}>
                                    {grouped.byProduct.map(d => (
                                        <DiscountRow key={d.id} d={d} onPick={pickFromCatalog} accent="var(--joy-palette-primary-500)" />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {[...grouped.byCategory.entries()].map(([catId, arr]) => {
                            const cat = categoryById[catId];
                            const accent = cat?.color || '#f59e0b';
                            return (
                                <Box key={catId}>
                                    <Typography sx={sectionLabelSx}>
                                        <CategoryRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                        Категория «{cat?.name || `#${catId}`}»
                                    </Typography>
                                    <Stack spacing={0.6}>
                                        {arr.map(d => (
                                            <DiscountRow key={d.id} d={d} onPick={pickFromCatalog} accent={accent} />
                                        ))}
                                    </Stack>
                                </Box>
                            );
                        })}

                        {grouped.universal.length > 0 && (
                            <Box>
                                <Typography sx={sectionLabelSx}>
                                    <PublicRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                    Универсальные
                                </Typography>
                                <Stack spacing={0.6}>
                                    {grouped.universal.map(d => (
                                        <DiscountRow key={d.id} d={d} onPick={pickFromCatalog} accent="#22c55e" />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {availableDiscounts.length === 0 && (
                            <Box sx={{ py: 4, textAlign: 'center' }}>
                                <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-text-tertiary)' }}>
                                    Подходящих скидок из справочника нет
                                </Typography>
                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.5 }}>
                                    Можно ввести скидку вручную — переключи режим выше
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                )}

                {/* ── Ручной ввод ────────────────────────────────── */}
                {mode === 'manual' && (
                    <Stack spacing={1.5}>
                        <Box>
                            <Typography sx={fieldLabelSx}>Название</Typography>
                            <Input value={manualName} onChange={e => setManualName(e.target.value.slice(0, 100))}
                                placeholder="Скидка ДР / по акции"
                            />
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={fieldLabelSx}>Размер *</Typography>
                                <NumericInput type="number" inputMode="decimal" value={manualValue} onChange={e => setManualValue(e.target.value)}
                                    placeholder="100"
                                    endDecorator={
                                        <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                            {manualIsPercent ? '%' : '₽'}
                                        </Typography>
                                    } />
                            </Box>
                            <Stack direction="row" spacing={0.3}>
                                <Button size="sm"
                                    variant={!manualIsPercent ? 'solid' : 'outlined'}
                                    color={!manualIsPercent ? 'warning' : 'neutral'}
                                    onClick={() => setManualIsPercent(false)}
                                    sx={{ minWidth: 38 }}>₽</Button>
                                <Button size="sm"
                                    variant={manualIsPercent ? 'solid' : 'outlined'}
                                    color={manualIsPercent ? 'warning' : 'neutral'}
                                    onClick={() => setManualIsPercent(true)}
                                    sx={{ minWidth: 38 }}>%</Button>
                            </Stack>
                        </Box>
                        <Button
                            disabled={!manualValue || parseFloat(manualValue) <= 0}
                            onClick={applyManual}
                            sx={{
                                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                                color: '#fff', fontWeight: 700,
                            }}>
                            Применить скидку
                        </Button>
                    </Stack>
                )}
            </ModalDialog>
        </Modal>
    );
}

// ── вспомогательная строка-выбор ─────────────────────────────
function DiscountRow({ d, onPick, accent }) {
    const fmt = `${d.value}${d.is_percent ? '%' : '₽'}`;
    return (
        <Box onClick={() => onPick(d)}
            sx={{
                p: 1.2, cursor: 'pointer',
                borderRadius: 'var(--joy-radius-md)',
                border: '1px solid var(--joy-palette-divider)',
                bgcolor: 'var(--joy-palette-background-level2)',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                transition: 'all .15s',
                '&:hover': {
                    borderColor: accent,
                    bgcolor: `${accent}14`,
                    transform: 'translateX(2px)',
                },
            }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                {d.name}
            </Typography>
            <Chip size="sm" sx={{ bgcolor: `${accent}22`, color: accent, fontWeight: 800 }}>
                {fmt}
            </Chip>
        </Box>
    );
}

const sectionLabelSx = {
    fontSize: 10, fontWeight: 700,
    color: 'var(--joy-palette-text-tertiary)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    mb: 0.6,
};
const fieldLabelSx = {
    fontSize: 11, fontWeight: 600,
    color: 'var(--joy-palette-text-tertiary)',
    mb: 0.4,
};